import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import lodashGet from 'lodash/get';
import i18n from '../../utils/i18n';
import SimpleModal from '@adsk/bim360-matrix-react-simple-modal';
import Button from '@adsk/bim360-matrix-react-button';
import Loader from '@adsk/bim360-matrix-react-loader';
import Checkbox from '@adsk/bim360-matrix-react-checkbox';
import InfoPopover from '@adsk/bim360-matrix-react-info-popover';
import TemplateEditFormContainer from './TemplateEditFormContainer';
import {TEMPLATE_FIELD_NAMES, TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES} from '../../constants/issue-templates';
import {parsePendingTemplateToBeStructure} from '../../utils/issue-template-helpers';

class TemplateEditModal extends PureComponent {
  constructor(...args) {
    super(...args);
    this.state = {
      createAnother: false,
      isSubmitting: false,
      isPopupOpen: false
    };

    this.formRef = React.createRef();
    this.requiredFieldMessage = i18n.t('new.fieldRequired');
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isOpen && !this.props.isOpen) {
      this.resetForm();
    }
  }

  componentWillUnmount() {
    this.resetForm();
  }

  onPopupOpen = () => this.setState({isPopupOpen: true});

  onPopupClose = () => this.setState({isPopupOpen: false});

  resetForm() {
    this.props.resetFormAction('pendingTemplate');
    this.props.resetValidityFormAction('pendingTemplate');
  }

  handleSubmit = () => {
    const {
      pendingTemplateForm,
      pendingTemplate,
      fieldIssueTypeEntitiesMap,
      issueCustomAttributesMappings,
      setSubmitFailedFormAction,
      localStorageKey,
      templateId,
      createTemplateAction,
      updateTemplateAction
    } = this.props;
    if (pendingTemplateForm.valid) {
      this.setState({isSubmitting: true});
      const templateForBE = parsePendingTemplateToBeStructure(
        pendingTemplate,
        fieldIssueTypeEntitiesMap,
        issueCustomAttributesMappings
      );
      if (templateId) {
        updateTemplateAction(templateId, templateForBE, {
          onSuccess: this.handleSaveSuccess,
          onError: this.handleSaveError
        });
      } else {
        createTemplateAction(templateForBE, {onSuccess: this.handleSaveSuccess, onError: this.handleSaveError});
      }

      if (localStorageKey) {
        const localStorageValues = {
          [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.SUBTYPE_ID]:
            pendingTemplate[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.SUBTYPE_ID],
          [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.STATUS]: pendingTemplate[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.STATUS],
          [TEMPLATE_FIELD_NAMES.CATEGORY_ID]: pendingTemplate[TEMPLATE_FIELD_NAMES.CATEGORY_ID]
        };
        localStorage.setItem(localStorageKey, JSON.stringify(localStorageValues));
      }
    } else {
      setSubmitFailedFormAction('pendingTemplate', true);
      const scrollToFirstError = lodashGet(this.formRef.current, 'wrappedInstance.scrollToFirstError');
      scrollToFirstError && scrollToFirstError();
    }
  };

  onCreateAnother() {
    const {setErrorsFormAction, changeFormValueAction, setSubmitFailedFormAction} = this.props;
    setSubmitFailedFormAction('pendingTemplate', false);
    changeFormValueAction(`pendingTemplate['${TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.TITLE}']`, '');
    setErrorsFormAction(`pendingTemplate['${TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.TITLE}']`, {
      submitFailed: this.requiredFieldMessage
    });

    const scrollFormToTop = lodashGet(this.formRef.current, 'wrappedInstance.scrollToTop');
    scrollFormToTop && scrollFormToTop();
  }

  handleSaveSuccess = response => {
    this.setState({
      isSubmitting: false
    });
    if (this.state.createAnother) {
      this.onCreateAnother();
    } else {
      this.props.onClose();
    }
    this.props.onSaveSuccess(response);
  };

  handleSaveError = () => {
    this.setState({
      isSubmitting: false
    });
    this.props.onSaveError();
  };

  toggleCreateAnother = () => {
    this.setState({createAnother: !this.state.createAnother});
  };

  renderFooter() {
    const {onClose, allowCreateAnother, saveButtonText, savingButtonText, disableSaveButton, container} = this.props;
    const {isSubmitting, isPopupOpen} = this.state;

    const footerClasses = cx('TemplateEditModal__footer', {
      'TemplateEditModal__footer--shadowed': isPopupOpen
    });
    const saveButtonClasses = cx('TemplateEditModal__create-button', {
      'TemplateEditModal__create-button--saving': isSubmitting
    });

    return (
      <div className={footerClasses}>
        <div className="TemplateEditModal__footer-create-another">
          {allowCreateAnother && (
            <React.Fragment>
              <Checkbox
                onChange={this.toggleCreateAnother}
                checked={this.state.createAnother}
                label={i18n.t('createTemplate.createAnother')}
              />
              <InfoPopover
                message={i18n.t('createTemplate.createAnotherTooltip')}
                container={container}
                tooltipClassName="TemplateEditModal__footer-create-another-tooltip"
              />
            </React.Fragment>
          )}
        </div>
        <Button styleType={Button.StyleType.LINK_SECONDARY} onClick={onClose}>
          {i18n.t('navigation.cancel')}
        </Button>
        <Button
          styleType={Button.StyleType.PRIMARY}
          className={saveButtonClasses}
          onClick={this.handleSubmit}
          disabled={isSubmitting || disableSaveButton}
          data-testid="TemplateEditModal__submit-btn"
        >
          {isSubmitting ? savingButtonText : saveButtonText}
        </Button>
      </div>
    );
  }

  render() {
    const {container, onClose, isOpen, title, loading} = this.props;
    const {isSubmitting} = this.state;

    return (
      <SimpleModal
        show={isOpen}
        title={title}
        className="TemplateEditModal"
        container={container}
        onExited={onClose}
        disableModalExit={isSubmitting}
        footer={this.renderFooter()}
        backdrop="static"
      >
        {loading ? (
          <Loader />
        ) : (
          <div className="TemplateEditModal__form">
            <TemplateEditFormContainer
              ref={this.formRef}
              onSubmit={this.handleSubmit}
              disabled={isSubmitting}
              container={container}
              initialValues={this.props.initialValues}
              localStorageKey={this.props.localStorageKey}
              onPopupOpen={this.onPopupOpen}
              onPopupClose={this.onPopupClose}
            />
          </div>
        )}
      </SimpleModal>
    );
  }
}

TemplateEditModal.displayName = 'TemplateEditModal';

TemplateEditModal.propTypes = {
  /**
   * Template ID - if provided - we update a template. else - we create a new one.
   */
  templateId: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  disableSaveButton: PropTypes.bool,
  saveButtonText: PropTypes.string.isRequired,
  savingButtonText: PropTypes.string.isRequired,
  container: PropTypes.object,
  allowCreateAnother: PropTypes.bool,
  initialValues: PropTypes.object,
  localStorageKey: PropTypes.string,
  pendingTemplate: PropTypes.object.isRequired,
  pendingTemplateForm: PropTypes.object.isRequired,
  issueCustomAttributesMappings: PropTypes.object.isRequired,
  fieldIssueTypeEntitiesMap: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaveSuccess: PropTypes.func.isRequired,
  onSaveError: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  setSubmitFailedFormAction: PropTypes.func.isRequired,
  resetFormAction: PropTypes.func.isRequired,
  resetValidityFormAction: PropTypes.func.isRequired,
  setErrorsFormAction: PropTypes.func.isRequired,
  changeFormValueAction: PropTypes.func.isRequired,
  createTemplateAction: PropTypes.func.isRequired,
  updateTemplateAction: PropTypes.func.isRequired
};

export default TemplateEditModal;
